import React, { useEffect, useMemo, useState } from 'react';
import { portalApi } from '../api';
import { usePortalAuth } from '../context/PortalAuthContext';
import { toast } from 'sonner';

const ACCOUNTING_EMAIL = 'accounting@utis.local';

export default function PortalInvoices() {
  const { session, token } = usePortalAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLinks, setPaymentLinks] = useState({});
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploadState, setUploadState] = useState('idle');

  const outstandingTotal = useMemo(
    () => invoices.reduce((total, invoice) => total + (invoice.outstanding_amount || 0), 0),
    [invoices]
  );

  const loadInvoices = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await portalApi.listInvoices(token);
      setInvoices(data);
      setError(null);
    } catch (loadError) {
      const detail = loadError?.response?.data?.detail || 'Unable to fetch invoices';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [token]);

  const handlePaymentLinks = async (invoiceId) => {
    try {
      const response = await portalApi.fetchPaymentLinks(invoiceId, token);
      setPaymentLinks((previous) => ({ ...previous, [invoiceId]: response.links }));
      toast.success('Payment links ready');
    } catch (linkError) {
      const detail = linkError?.response?.data?.detail || 'Payment link unavailable';
      toast.error(detail);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!file) {
      toast.error('Choose a document to share');
      return;
    }
    setUploadState('uploading');
    try {
      await portalApi.uploadDocument({ file, description }, token);
      setUploadState('success');
      setFile(null);
      setDescription('');
      toast.success('Document shared with UTIS');
    } catch (uploadError) {
      setUploadState('error');
      const detail = uploadError?.response?.data?.detail || 'Upload failed';
      toast.error(detail);
    }
  };

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Welcome back</p>
            <h2 className="text-2xl font-semibold">{session?.client_name || 'Client contact'}</h2>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Open balance</p>
            <p className="text-3xl font-bold text-emerald-600">${outstandingTotal.toFixed(2)}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadInvoices}
          className="mt-4 text-sm text-indigo-600 font-medium"
        >
          Refresh list
        </button>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Invoices</h3>
          {loading && <span className="text-sm text-slate-500">Loading…</span>}
        </div>
        {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p>}
        {invoices.map((invoice) => (
          <article key={invoice.invoice_id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">Invoice</p>
                <p className="text-xl font-semibold text-slate-900">{invoice.invoice_no}</p>
                <p className="text-sm text-slate-500">
                  Issued {invoice.invoice_date} · Due {invoice.due_date}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">{invoice.currency} {invoice.total.toFixed(2)}</p>
                <p className="text-sm text-amber-600">Due {invoice.outstanding_amount.toFixed(2)}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                className="rounded-md border border-indigo-200 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                onClick={() => handlePaymentLinks(invoice.invoice_id)}
              >
                View payment links
              </button>
              <a
                href={`mailto:${ACCOUNTING_EMAIL}?subject=Invoice ${invoice.invoice_no}`}
                className="rounded-md border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
              >
                Email accounting
              </a>
            </div>
            {paymentLinks[invoice.invoice_id] && (
              <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                <p className="mb-2 font-medium text-slate-700">Payment providers</p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(paymentLinks[invoice.invoice_id]).map(([provider, url]) => (
                    <a
                      key={provider}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 underline"
                    >
                      {provider}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>
        ))}
        {!loading && invoices.length === 0 && (
          <p className="rounded border border-dashed border-slate-200 p-6 text-center text-slate-500">
            No invoices found for your organization.
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold">Share supporting documents</h3>
        <p className="text-sm text-slate-500">Upload purchase orders or remittance advice securely.</p>
        <form onSubmit={handleUpload} className="mt-4 space-y-4">
          <div>
            <label htmlFor="portal-document-upload" className="text-sm font-medium text-slate-600">
              Supporting document
            </label>
            <input
              id="portal-document-upload"
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-slate-600"
            />
          </div>
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Description (optional)"
            className="w-full rounded border border-slate-200 px-3 py-2"
          />
          <button
            type="submit"
            className="rounded-md bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
          >
            {uploadState === 'uploading' ? 'Uploading…' : 'Share document'}
          </button>
          {uploadState === 'success' && (
            <p className="text-sm text-emerald-600">Document uploaded successfully.</p>
          )}
          {uploadState === 'error' && (
            <p className="text-sm text-rose-600">We could not upload the file. Try again.</p>
          )}
        </form>
      </section>
    </div>
  );
}
